import React, { useEffect, useRef, useState } from 'react';
import * as L from 'leaflet';
import 'leaflet.vectorgrid';
import { useMap } from 'react-leaflet';
import { config } from '../../utils/config';
import { patchLeafletVectorGridFetch } from '../../utils/leafletVectorGridPatch';

patchLeafletVectorGridFetch();

interface PropertyLinesLayerProps {
  visible?: boolean;
}

const PropertyLinesLayer: React.FC<PropertyLinesLayerProps> = ({ visible = true }) => {
  const map = useMap();
  const vectorTileLayerRef = useRef<any>(null);
  const [tileError, setTileError] = useState<boolean>(false);
  const zoomTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const panTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Removed unused isLoading state
  const [currentZoom, setCurrentZoom] = useState<number>(map.getZoom());

  // Function to create the property layer
  const createPropertyLayer = () => {
    if (vectorTileLayerRef.current) return; // Already exists
    
    try {
      // Detect device characteristics for optimal rendering
      const devicePixelRatio = window.devicePixelRatio || 1;
      const isMobile = window.innerWidth < 768;
      
      // Calculate optimal line weight for device
      const getOptimalLineWeight = () => {
        if (!isMobile) return 0.3; // Desktop - keep thin
        
        // Mobile: scale with DPR but keep minimum readability
        if (devicePixelRatio >= 3) return 0.6; // 3x displays (iPhone Plus, etc)
        if (devicePixelRatio >= 2) return 0.5; // 2x displays (most modern phones)
        return 0.4; // 1x displays (older devices)
      };

      const vectorTileOptions = {
        vectorTileLayerStyles: {
          property_lines: {
            weight: getOptimalLineWeight(),
            color: '#666666', // Lighter gray instead of black
            opacity: 0.5, // More transparent
            fill: false,
            className: 'property-line'
          }
        },
        // Match actual tile data zoom levels
        maxNativeZoom: 14, // Property tiles only go to zoom 14
        maxZoom: 20, // Allow zooming beyond tile data
        minZoom: 10, // Property tiles start at zoom 10
        interactive: false,
        // Light caching settings
        updateWhenZooming: false,
        updateWhenIdle: true,
        zIndex: 100,
        getIDForLayerFeature: () => 'property',
        maxRequests: window.innerWidth < 768 ? 3 : 6,
        tileSize: 256,
        attribution: '&copy; City of Toronto Property Data'
      };

      const vectorTileLayer = (L as any).vectorGrid.protobuf(
        `${config.TILES_URL}/data/toronto_properties/{z}/{x}/{y}.pbf`,
        vectorTileOptions
      );

      vectorTileLayer.addTo(map);
      vectorTileLayerRef.current = vectorTileLayer;
      updateLayerOpacity();
      
    } catch (error) {
      console.error('Failed to create vector tile layer:', error);
      setTileError(true);
    }
  };

  // Function to update layer opacity based on zoom level
  const updateLayerOpacity = () => {
    if (!vectorTileLayerRef.current) return;
    
    const zoom = map.getZoom();
          const minZoomThreshold = 16; // Show property lines starting at zoom 16 (more zoomed in)
    
    if (zoom >= minZoomThreshold && visible) {
      vectorTileLayerRef.current.setOpacity(0.5); // Lighter opacity
    } else {
      vectorTileLayerRef.current.setOpacity(0);
    }
  };

  // Track zoom changes and manage layer creation/removal
  useEffect(() => {
    if (!map) return;
    
    const handleZoomEnd = () => {
      const newZoom = map.getZoom();
      setCurrentZoom(newZoom);
      
      const preloadZoom = 15; // Preload at zoom 15, show at zoom 16
      
      // Create layer if we've zoomed in enough and don't have one
      if (!vectorTileLayerRef.current && newZoom >= preloadZoom && visible) {
        createPropertyLayer();
      }
      // Remove layer if we've zoomed out too far
      else if (vectorTileLayerRef.current && newZoom < preloadZoom) {
        map.removeLayer(vectorTileLayerRef.current);
        vectorTileLayerRef.current = null;
      }
      // Update opacity if layer exists
      else if (vectorTileLayerRef.current) {
        updateLayerOpacity();
      }
    };
    
    map.on('zoomend', handleZoomEnd);
    return () => {
      map.off('zoomend', handleZoomEnd);
    };
  }, [map, visible]);

  // Initial layer creation on mount
  useEffect(() => {
    if (!map || !visible) return;

    const currentZoom = map.getZoom();
    const preloadZoom = window.innerWidth < 768 ? 15 : 16;

    // Create layer if we're already at the right zoom level
    if (currentZoom >= preloadZoom) {
      createPropertyLayer();
    }

    // Clean up on unmount
    return () => {
      // Clear any pending timeouts
      if (zoomTimeoutRef.current) {
        clearTimeout(zoomTimeoutRef.current);
      }
      if (panTimeoutRef.current) {
        clearTimeout(panTimeoutRef.current);
      }
      if (map && vectorTileLayerRef.current) {
        map.removeLayer(vectorTileLayerRef.current);
        vectorTileLayerRef.current = null;
      }
    };
  }, [map, visible]);

  // Provide user feedback for tile loading states
  if (tileError) {
    console.warn('Property lines layer failed to load - continuing without property lines');
  }

  return null;
};

export default PropertyLinesLayer;
